import BigLogo from "@assets/images/CESIF_METRODORA_COLOR.png";
import { changeLanguage } from "@i18n/i18n";
import keycloakClient from "@keycloak/keycloak";
import { Box, Button, CircularProgress, LinearProgress } from "@mui/material";
import {
  AuthClientError,
  AuthClientEvent
} from "@react-keycloak/core/lib/types";
import { ReactKeycloakProvider, useKeycloak } from "@react-keycloak/web";
import { chatManagerApiService } from "@services/chatManagerApi/ChatManagerApiService";
import Keycloak from "keycloak-js";
import { ReactNode, useEffect, useState } from "react";

const CUSTOM_VALIDATED_SESSION_KEY = "isCustomValidated";
const ADMIN_ROLE = "cesif_ui_administrator";

// ---------------------------------------------
// Detect if running inside an iframe
function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

// ---------------------------------------------
const performApiValidation = async (keycloak: Keycloak) => {
  try {
    const email = keycloak.tokenParsed?.email;
    const token = keycloak.token;
    if (!email || !token)
      throw new Error("Email or token not found in Keycloak.");
    const userData = await chatManagerApiService.validateBlackboardUserInfo(
      email,
      token
    );
    try {
      await chatManagerApiService.getBlackboardUserMasters(
        userData.userName,
        token
      );
    } catch (mastersError) {
      console.warn(
        "Failed to get user masters, continuing anyway.",
        mastersError
      );
    }
    // Only try to refresh token if not in iframe context
    // In iframe, token refresh often fails due to cookie domain issues, but original token is still valid
    if (!isInIframe()) {
      try {
        await keycloak.updateToken(-1);
      } catch (tokenError) {
        console.warn("Token refresh failed:", tokenError);
      }
    } else {
      // In iframe, skip token refresh - the original token from popup should still be valid
      console.log(
        "Skipping token refresh in iframe context (original token should still be valid)"
      );
    }
    console.log("Custom API validation successful.");
    return true;
  } catch (error) {
    console.error("Custom API validation failed.", error);
    return false;
  }
};

const useCustomValidation = () => {
  const { keycloak, initialized } = useKeycloak();
  const [isCustomValidated, setCustomValidated] = useState(false);
  const [isValidating, setValidating] = useState(true);

  useEffect(() => {
    const handleValidation = async () => {
      if (!initialized) {
        setValidating(true);
        return;
      }
      if (!keycloak.authenticated) {
        sessionStorage.removeItem(CUSTOM_VALIDATED_SESSION_KEY);
        setValidating(false);
        return;
      }
      if (keycloak.hasRealmRole(ADMIN_ROLE)) {
        sessionStorage.setItem(CUSTOM_VALIDATED_SESSION_KEY, "true");
        setCustomValidated(true);
        setValidating(false);
        return;
      }
      if (sessionStorage.getItem(CUSTOM_VALIDATED_SESSION_KEY) === "true") {
        setCustomValidated(true);
        setValidating(false);
        return;
      }
      setValidating(true);
      const wasValidationSuccessful = await performApiValidation(keycloak);
      if (wasValidationSuccessful) {
        sessionStorage.setItem(CUSTOM_VALIDATED_SESSION_KEY, "true");
        setCustomValidated(true);
      } else {
        sessionStorage.removeItem(CUSTOM_VALIDATED_SESSION_KEY);
        await keycloak.logout({ redirectUri: window.location.origin });
      }
      setValidating(false);
    };
    handleValidation();
  }, [initialized, keycloak]);
  return { isValidating, isCustomValidated, initialized };
};

// --- UI Components ---
const LoadingSpinner = () => (
  <Box className="flex w-screen h-screen flex-col items-center justify-center gap-4">
    <img
      src={BigLogo}
      alt="big_logo"
      width={180}
      style={{ marginBottom: 10 }}
    />
    <CircularProgress color="primary" style={{ marginBottom: 10 }} />
  </Box>
);

const AuthFailure = () => (
  <Box className="flex w-screen h-screen items-center justify-center">
    <p>La validación del usuario ha fallado. Serás redirigido</p>
  </Box>
);

function CustomValidatedApp({ children }: { children: ReactNode }) {
  const { isValidating, isCustomValidated, initialized } =
    useCustomValidation();

  if (!initialized || isValidating) return <LoadingSpinner />;
  if (isCustomValidated) return <>{children}</>;
  return <AuthFailure />;
}

export const options = {
  onLoad: "login-required",
  checkLoginIframe: false
};

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const handleKeycloakEvent = (
    event: AuthClientEvent,
    error?: AuthClientError
  ) => {
    console.log("[SSO][KeycloakEvent]", event);
    if (event === "onAuthSuccess") {
      const preferredLanguage = "es";
      changeLanguage(preferredLanguage);
      // If this is a popup window, send tokens back to iframe
      if (window.opener) {
        try {
          window.opener.postMessage(
            {
              type: "authToken",
              token: keycloakClient.token,
              refreshToken: keycloakClient.refreshToken
            },
            window.location.origin
          );
          setTimeout(() => window.close(), 300);
        } catch (err) {
          console.warn("Error sending auth tokens to opener");
        }
      }
    }
    if (event === "onAuthLogout") {
      keycloakClient.clearToken();
      // Clear stored tokens from sessionStorage
      if (isInIframe()) {
        sessionStorage.removeItem(IFRAME_AUTH_KEY);
        sessionStorage.removeItem(KC_TOKEN_KEY);
        sessionStorage.removeItem(KC_REFRESH_TOKEN_KEY);
        // Recargar para volver a flujo de login si está embebido
        window.location.reload();
      }
    } else if (event === "onAuthRefreshError") {
      // Token refresh failed - this is expected in iframe context
      // The original token from popup should still be valid, so don't logout
      console.warn("[SSO] Token refresh failed (expected in iframe):", error);
      // Don't reload or logout - the original token should still work
      // Only reload if we're completely unauthenticated AND don't have a stored token
      const hasStoredToken = isInIframe() && sessionStorage.getItem("kc_token");
      if (!keycloakClient.authenticated && isInIframe() && !hasStoredToken) {
        console.warn("[SSO] Not authenticated and no stored token, reloading");
        setTimeout(() => window.location.reload(), 1000);
      } else if (!keycloakClient.authenticated && hasStoredToken) {
        // We have a stored token but Keycloak thinks we're not authenticated
        // This might be a timing issue - don't reload immediately
        console.log("[SSO] Refresh failed but stored token exists, continuing");
      }
    } else if (event === "onAuthError") {
      console.warn("[SSO] Auth error occurred:", error);
      // Clear stored tokens on auth error
      if (isInIframe()) {
        sessionStorage.removeItem(IFRAME_AUTH_KEY);
        sessionStorage.removeItem(KC_TOKEN_KEY);
        sessionStorage.removeItem(KC_REFRESH_TOKEN_KEY);
        window.location.reload();
      }
    }
  };

  // ---------- Popup approach for iframe (Azure AD blocks iframes) ----------
  // Azure AD sets X-Frame-Options: deny, so it cannot be embedded in iframe
  // Solution: Use popup for authentication, then return to iframe
  const insideIframe = isInIframe();
  const IFRAME_AUTH_KEY = "iframe_auth_success";
  const KC_TOKEN_KEY = "kc_token";
  const KC_REFRESH_TOKEN_KEY = "kc_refresh_token";

  const storedToken = insideIframe
    ? (sessionStorage.getItem(KC_TOKEN_KEY) ?? undefined)
    : undefined;
  const storedRefresh = insideIframe
    ? (sessionStorage.getItem(KC_REFRESH_TOKEN_KEY) ?? undefined)
    : undefined;
  const iframeAuth =
    insideIframe &&
    sessionStorage.getItem(IFRAME_AUTH_KEY) === "true" &&
    !!storedToken;

  console.log(
    "[SSO][AuthProvider] insideIframe:",
    insideIframe,
    "iframeAuth:",
    iframeAuth
  );

  // Listen for auth tokens from popup (must be at top level, not conditional)
  useEffect(() => {
    if (!insideIframe) return;

    const handleAuthMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "authToken") {
        try {
          sessionStorage.setItem(IFRAME_AUTH_KEY, "true");
          if (event.data.token)
            sessionStorage.setItem(KC_TOKEN_KEY, event.data.token);
          if (event.data.refreshToken)
            sessionStorage.setItem(
              KC_REFRESH_TOKEN_KEY,
              event.data.refreshToken
            );
        } catch (err) {
          console.warn("Error persisting tokens");
        }
        window.location.reload();
      }
    };
    window.addEventListener("message", handleAuthMessage);
    return () => window.removeEventListener("message", handleAuthMessage);
  }, [insideIframe]);

  // If in iframe and not authenticated, show popup button
  if (insideIframe && !iframeAuth) {
    const handleOpenPopup = () => {
      const popupWidth = 1200;
      const popupHeight = 670;
      const left = (window.screen.width - popupWidth) / 2;
      const top = (window.screen.height - popupHeight) / 2;
      window.open(
        window.location.origin,
        "CesifAuthPopup",
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
    };

    return (
      <Box className="flex w-screen h-screen flex-col items-center justify-center gap-4">
        <img
          src={BigLogo}
          alt="big_logo"
          width={180}
          style={{ marginBottom: 10 }}
        />
        <Button
          fullWidth
          variant="contained"
          onClick={handleOpenPopup}
          sx={{
            py: 1,
            borderRadius: 2.5,
            textTransform: "none",
            fontSize: 16,
            fontWeight: 600,
            width: "30%",
            bgcolor: "#276EF1",
            "&:hover": { bgcolor: "#276EF1" }
          }}
        >
          Iniciar sesión
        </Button>
      </Box>
    );
  }

  // Use stored tokens if available, otherwise normal flow
  const initOptions =
    iframeAuth && storedToken && storedRefresh
      ? {
          ...options,
          onLoad: "check-sso",
          token: storedToken,
          refreshToken: storedRefresh,
          checkLoginIframe: false
        }
      : options;

  return (
    <ReactKeycloakProvider
      authClient={keycloakClient}
      initOptions={initOptions}
      onEvent={handleKeycloakEvent}
      LoadingComponent={
        <Box className="flex w-screen h-screen items-center justify-center">
          <LinearProgress color="primary" />
        </Box>
      }
    >
      <CustomValidatedApp>{children}</CustomValidatedApp>
    </ReactKeycloakProvider>
  );
}
